import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { Button, Modal, Text } from '@mantine/core';
import { ModalContent } from '@shipstation/client-common/components/modal-content';
import { ModalFooter } from '@shipstation/client-common/components/modal-footer';

import { freightBookingWizardContainerMessages as messages } from '../../messages';
import styles from './confirm-close-modal.module.css';

type ConfirmCloseModalProps = {
  isOpen: boolean;
  isSavingDraft: boolean;
  zIndex: number;
  onClose: () => void;
  /** Omit when the user lacks the FreightBook permission — the Save Draft option is then not offered. */
  onSaveDraft?: () => void;
  onDiscard: () => void;
};

export const ConfirmCloseModal = ({ isOpen, isSavingDraft, zIndex, onClose, onSaveDraft, onDiscard }: ConfirmCloseModalProps) => {
  const handleSaveDraft = useCallback(() => {
    if (!onSaveDraft) return;
    onClose();
    onSaveDraft();
  }, [onClose, onSaveDraft]);
  const handleDiscard = useCallback(() => {
    onClose();
    onDiscard();
  }, [onClose, onDiscard]);

  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      title={<FormattedMessage {...messages.confirmCloseHeader} />}
      size='sm'
      zIndex={zIndex + 1}
      classNames={{ close: styles.close }}
    >
      <ModalContent>
        <Text>
          <FormattedMessage {...messages.confirmCloseBody} />
        </Text>
      </ModalContent>
      <ModalFooter>
        {onSaveDraft && (
          <Button variant='tertiary' onClick={handleSaveDraft} disabled={isSavingDraft}>
            <FormattedMessage {...messages.confirmCloseSaveDraft} />
          </Button>
        )}
        <Button variant='tertiary' onClick={onClose}>
          <FormattedMessage {...messages.confirmCloseKeepEditing} />
        </Button>
        <Button color='destructive' variant='secondary' onClick={handleDiscard} disabled={isSavingDraft}>
          <FormattedMessage {...messages.confirmCloseDiscard} />
        </Button>
      </ModalFooter>
    </Modal>
  );
};
